import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function formatError(detail) {
  if (!detail) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="login-page"
      className="relative flex min-h-screen items-center justify-center bg-[#050505] px-5 grid-lines"
    >
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-cyanbrand/10 blur-3xl" />
      <motion.form
        onSubmit={submit}
        data-testid="login-form"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md border border-white/10 bg-[#0a0a0a] p-8 md:p-10"
      >
        <div className="mb-8 flex items-center gap-2.5 text-white">
          <LogoMark className="h-9 w-9" />
          <span className="font-display text-lg font-semibold tracking-tight">
            SC<span className="text-cyanbrand">·</span>Security
          </span>
        </div>

        <span className="font-mono text-xs uppercase tracking-[0.2em] text-cyanbrand">
          / Back office
        </span>
        <h1 className="mt-3 font-display text-3xl font-medium tracking-tight text-white">
          Staff login
        </h1>
        <p className="mt-2 font-body text-sm text-white/50">
          Sign in to review and respond to quote requests.
        </p>

        <div className="mt-8 space-y-2">
          <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">Email</Label>
          <Input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.co.uk"
            className="h-12 rounded-none border-white/15 bg-black text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-cyanbrand"
          />
        </div>
        <div className="mt-5 space-y-2">
          <Label className="font-mono text-[11px] uppercase tracking-wider text-white/50">Password</Label>
          <Input
            data-testid="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="h-12 rounded-none border-white/15 bg-black text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-cyanbrand"
          />
        </div>

        {error && (
          <p data-testid="login-error" className="mt-4 border border-redbrand/40 bg-redbrand/10 px-3 py-2 font-body text-sm text-redbrand">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="mt-8 flex w-full items-center justify-center gap-2 bg-cyanbrand py-4 font-semibold text-black transition-transform duration-200 hover:scale-[1.01] disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          {loading ? "Signing in…" : "Sign in"}
        </button>

        <a href="/" className="mt-6 block text-center font-mono text-[11px] uppercase tracking-wider text-white/40 hover:text-cyanbrand transition-colors">
          ← Back to site
        </a>
      </motion.form>
    </main>
  );
}
